import React, { useState } from "react";
import { Check, PencilLine } from "lucide-react";

import { useRequestProcessor } from "../../requestProcessor";
import { getSurveyParams } from "../../queryfunctions/queryfunctions";
import MyCenterBg from "../../assets/mycenter.jpg";
import NpsTeacher from "../../assets/Nps/teacher.png";
import NpsSchool from "../../assets/Nps/school.png";
import NpsKindergarten from "../../assets/Nps/kindergarten.png";
import NpsAlarmClock from "../../assets/Nps/alarm-clock.png";
import NpsBill from "../../assets/Nps/bill.png";

const SurveySkeleton = () => (
    <div className="w-full animate-pulse bg-white">
        {/* Hero */}
        <div className="min-h-[230px] bg-gray-200" />

        <div className="-mt-28 px-3 pb-3 sm:px-4">
            <div className="relative rounded-2xl bg-white px-2 py-3 sm:px-4">
                {/* Question text */}
                <div className="mb-2 h-4 w-3/4 rounded bg-gray-200" />
                <div className="mb-3 h-4 w-1/2 rounded bg-gray-200" />

                {/* Rating buttons */}
                <div className="grid grid-cols-10 gap-1">
                    {Array.from({ length: 10 }).map((_, i) => (
                        <div key={i} className="h-10 rounded-md bg-gray-200" />
                    ))}
                </div>
                <div className="mt-1 flex justify-between">
                    <div className="h-3 w-8 rounded bg-gray-200" />
                    <div className="h-3 w-14 rounded bg-gray-200" />
                </div>
            </div>
        </div>
    </div>
);

const NPS_ICON_MAP = [NpsTeacher, NpsSchool, NpsKindergarten, NpsAlarmClock, NpsBill];

const getNpsIcon = (item, index) => {
    const label = (item?.value || "").toLowerCase();
    if (label.includes("teacher")) return NpsTeacher;
    if (label.includes("director") || label.includes("center") || label.includes("school")) return NpsSchool;
    if (label.includes("ambience") || label.includes("ambiance") || label.includes("kindergarten")) return NpsKindergarten;
    if (label.includes("support") || label.includes("fast")) return NpsAlarmClock;
    if (label.includes("bill") || label.includes("fee") || label.includes("finance")) return NpsBill;
    return NPS_ICON_MAP[index % NPS_ICON_MAP.length];
};


const RIGHT_QUESTION = "What can we do for a better recommendation from you?";
const WRONG_QUESTION = "Please tell us what you like most about Footprints.";
const RATING_VALUES = Array.from({ length: 10 }, (_, index) => index + 1);

const getRatingTone = (value, isSelected) => {
    if (isSelected) {
        if (value <= 6) {
            return "border-red-500 bg-red-500 text-white shadow-md ring-2 ring-red-200 ring-offset-1";
        }
        if (value <= 8) {
            return "border-amber-500 bg-amber-500 text-white shadow-md ring-2 ring-amber-200 ring-offset-1";
        }
        return "border-emerald-500 bg-emerald-500 text-white shadow-md ring-2 ring-emerald-200 ring-offset-1";
    }

    if (value <= 6) {
        return "border-red-200 bg-white text-red-400 hover:border-red-300 hover:bg-red-50";
    }
    if (value <= 8) {
        return "border-amber-200 bg-white text-amber-500 hover:border-amber-300 hover:bg-amber-50";
    }
    return "border-emerald-200 bg-white text-emerald-500 hover:border-emerald-300 hover:bg-emerald-50";
};

const getTagImage = (item) =>
    item?.image || item?.image_url || item?.icon || item?.icon_url || item?.url;

const SurveyForm = ({ onRatingPressed, getTags, getComment, selectedChild }) => {
    const { useQueryWrapper } = useRequestProcessor();

    const [rating, setRating] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedCheckboxes, setSelectedCheckboxes] = useState({});
    const [comment, setComment] = useState("");

    const { data: questions, isLoading } = useQueryWrapper(
        ["getSurveyParams"],
        () => getSurveyParams(selectedChild)
    );

    const handleRatingChange = (val) => {
        const ratingVal = Number(val);

        // 1. update local rating
        setRating(ratingVal);

        // 2. find matching category for this rating
        const newSelectedItem = questions?.find(
            (item) =>
                ratingVal <= parseInt(item.cat_max, 10) &&
                ratingVal >= parseInt(item.cat_min, 10)
        ) || null;

        // 3. check if category changed
        const categoryChanged = selectedItem?.cat_key !== newSelectedItem?.cat_key;

        // 4. update selectedItem FIRST so UI re-renders with new tags
        setSelectedItem(newSelectedItem);

        // 5. clear checkboxes + notify parent
        if (categoryChanged) {
            setSelectedCheckboxes({});
            if (typeof getTags === "function") getTags([]);
        }

        // 6. notify parent rating change
        if (typeof onRatingPressed === "function") {
            onRatingPressed(ratingVal);
        }
    };


    const toggleTag = (item) => {
        if (!selectedItem) return;

        const key = selectedItem.cat_key;

        const wrapped = {
            value: item,
            label: item.value,
        };

        const current = selectedCheckboxes[key] || [];
        const isChecked = current.some((x) => x.value.value === item.value);

        let updated;
        if (isChecked) {
            updated = current.filter((x) => x.value.value !== item.value);
        } else {
            updated = [...current, wrapped];
        }

        const newState = { ...selectedCheckboxes, [key]: updated };
        setSelectedCheckboxes(newState);

        if (getTags) getTags(updated);
    };

    const handleCommentChange = (e) => {
        const txt = e.target.value;
        setComment(txt);
        if (typeof getComment === "function") getComment(txt);
    };

    if (isLoading) return <SurveySkeleton />;
    if (!questions) return <div />;

    return (
        <div className="relative z-0 w-full bg-white">
            <div className="relative min-h-[230px] overflow-hidden bg-cover bg-bottom px-7 py-4" style={{ backgroundImage: `url(${MyCenterBg})` }}>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[5px]"></div>
                <p className="relative w-full max-w-[330px] text-lg font-bold leading-snug text-black drop-shadow-sm md:max-w-none">
                    How likely would you recommend Footprints to your friends and colleagues?
                </p>
            </div>

            <div className="-mt-28 px-3 pb-3 sm:px-4">
                <div className="relative rounded-2xl bg-white px-2 py-3 sm:px-4">
                    <p className="mb-2 text-base font-semibold leading-snug text-gray-900">
                        Rate your experience?
                    </p>

                    <div className="grid grid-cols-10 gap-1">
                        {RATING_VALUES.map((value) => {
                            const isSelected = rating === value;

                            return (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => handleRatingChange(value)}
                                    className={`h-10 rounded-md border-2 text-lg font-bold transition-all ${getRatingTone(value, isSelected)}`}
                                    aria-pressed={isSelected}
                                >
                                    {value}
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-1 flex justify-between text-xs font-medium leading-tight text-gray-800">
                        <span>Not<br />Likely</span>
                        <span className="text-right">Extremely<br />Likely</span>
                    </div>

                    {selectedItem && (
                        <div className="mt-3">
                            <h3 className="mb-2 flex items-center gap-2 text-lg font-bold text-gray-900">
                                {selectedItem.cat_slug}
                                <PencilLine size={19} strokeWidth={2.2} />
                            </h3>

                            <div className="grid grid-cols-2 gap-2">
                                {selectedItem.data.map((item, idx) => {
                                    const isChecked =
                                        (selectedCheckboxes[selectedItem.cat_key] || []).some(
                                            (x) => x.value.value === item.value
                                        );
                                    const apiImage = getTagImage(item);
                                    const iconSrc = apiImage || getNpsIcon(item, idx);

                                    return (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => toggleTag(item)}
                                            className={`relative flex min-h-[106px] flex-col items-center justify-center rounded-xl border-2 px-3 py-3 text-center transition ${isChecked ? "border-cyan-500 bg-[#DDF2FA] shadow-md" : "border-[#C8E9F5] bg-[#EAF6FD] shadow-sm"}`}
                                            aria-pressed={isChecked}
                                        >
                                            {isChecked && (
                                                <span className="absolute right-2 top-2 rounded-full bg-cyan-600 p-0.5 text-white">
                                                    <Check size={14} strokeWidth={3} />
                                                </span>
                                            )}
                                            <img src={iconSrc} alt="" className="mb-2 h-12 w-12 object-contain" />
                                            <span className="text-base font-semibold leading-tight text-gray-900">{item.value}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {rating !== null && (
                        <div className="mt-3">
                            <label className="mb-1 block text-[15px] font-bold leading-snug tracking-[-0.01em] text-gray-900 sm:text-lg sm:tracking-normal">
                                {rating < 9 ? RIGHT_QUESTION : WRONG_QUESTION}
                            </label>
                            <textarea
                                className="h-24 w-full resize-none rounded-xl border border-gray-300 p-4 text-base outline-none transition placeholder:text-gray-400 focus:border-transparent focus:ring-2 focus:ring-cyan-500"
                                placeholder="Type your feedback here..."
                                value={comment}
                                onChange={handleCommentChange}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SurveyForm;
